/**
 * NeighborhoodModal Component
 *
 * A shared neighborhood modal for displaying neighborhood details across
 * Admin, Agent, and Public apps.
 */
import { formatNeighborhoodName, formatListingAddress } from '@slowmaui/shared-types';
import { injectStyles } from './styles.js';
import { NeighborhoodSections } from './NeighborhoodSections.js';
import { SalesChart } from '../SalesChart/index.js';
import { ActivityTable } from '../ActivityTable/index.js';
import { Disclaimers } from '../PropertyModal/Disclaimers.js';
// Content section definitions
const CONTENT_SECTIONS = [
    { id: 'overview', name: 'Overview', icon: '📋' },
    { id: 'insights', name: 'Local Insights', icon: '💡' },
    { id: 'market', name: 'Market Analysis', icon: '📊' },
    { id: 'around', name: 'What\'s Nearby', icon: '📍' },
    { id: 'distances', name: 'Getting Around', icon: '🚗' },
    { id: 'similar', name: 'Similar Communities', icon: '🏘️' },
    { id: 'resources', name: 'Resources', icon: '📚' },
];
export class NeighborhoodModal {
    options;
    data = null;
    neighborhood = null;
    listings = [];
    salesData = null;
    salesChart = null;
    activityTable = null;
    overlay = null;
    sections = null;
    disclaimers = null;
    salesDisclaimers = null;
    currentTab = 'overview';
    // Content tab (Agent Insight) state - see NeighborhoodModalOptions'
    // onLoadAgentNote/onSaveAgentNote.
    agentNoteId = null;
    agentNoteText = '';
    agentNoteStatus = null;
    agentNoteAdminNote = null;
    agentNoteLoaded = false;
    currentPhotoIndex = 0;
    expandedContentSections = new Set(['overview']); // Overview expanded by default
    // Fallback boundary map shown in the gallery slot when there are no photos
    // (see buildGallery/initGalleryMap) - mirrors GeographyModal's entityMap.
    galleryMap = null;
    galleryMapPolygon = null;
    constructor(options) {
        this.options = {
            showFavoriteButton: true,
            showAlertButton: true,
            showAgentPickButton: false,
            ...options,
        };
        injectStyles();
    }
    // initialTab lands directly on a non-overview tab (e.g. the "Change"
    // subscription email deep link opening straight to Latest Activity) -
    // reuses switchTab() itself rather than duplicating its tab-data-loading
    // branch here.
    async open(data, initialTab = 'overview') {
        this.data = data;
        this.currentTab = 'overview';
        this.currentPhotoIndex = 0;
        this.listings = [];
        this.salesData = null;
        this.neighborhood = null;
        this.activityTable?.destroy();
        this.activityTable = null;
        this.agentNoteId = null;
        this.agentNoteText = '';
        this.agentNoteStatus = null;
        this.agentNoteAdminNote = null;
        this.agentNoteLoaded = false;
        this.render();
        this.show();
        // Load full neighborhood data
        if (this.options.onLoadNeighborhoodData) {
            this.loadNeighborhoodData(data.id);
        }
        if (initialTab !== 'overview') {
            this.switchTab(initialTab);
        }
    }
    async loadNeighborhoodData(neighborhoodId) {
        if (!this.options.onLoadNeighborhoodData)
            return;
        try {
            const neighborhood = await this.options.onLoadNeighborhoodData(neighborhoodId);
            if (neighborhood) {
                this.neighborhood = neighborhood;
                this.updateWithNeighborhoodData();
            }
        }
        catch (error) {
            console.error('Failed to load neighborhood data:', error);
        }
    }
    async loadListings(neighborhoodId) {
        if (!this.options.onLoadListings)
            return;
        try {
            this.listings = await this.options.onLoadListings(neighborhoodId);
            this.updateListingsTab();
        }
        catch (error) {
            console.error('Failed to load listings:', error);
        }
    }
    async loadSalesData(neighborhoodId) {
        if (!this.options.onLoadSales)
            return;
        try {
            this.salesData = await this.options.onLoadSales(neighborhoodId);
            this.updateSalesTab();
        }
        catch (error) {
            console.error('Failed to load sales data:', error);
        }
    }
    updateWithNeighborhoodData() {
        if (!this.overlay || !this.neighborhood)
            return;
        // Update title
        const titleEl = this.overlay.querySelector('.sm-neighborhood-title');
        if (titleEl) {
            titleEl.textContent = formatNeighborhoodName(this.neighborhood.name);
        }
        // Update subtitle
        const subtitleEl = this.overlay.querySelector('.sm-neighborhood-subtitle');
        if (subtitleEl) {
            subtitleEl.textContent = this.neighborhood.regionId || '';
        }
        // Update stats
        this.updateStatsRow();
        // Update gallery
        this.updateGallery();
        // Update content sections (AI-generated)
        this.updateContentSections();
        // Update structured sections (Building, Amenities, etc.)
        this.updateSections();
        // Update disclaimers
        this.updateDisclaimers();
    }
    updateContentSections() {
        const container = this.overlay?.querySelector('#sm-neighborhood-content-sections');
        if (container) {
            container.innerHTML = this.buildContentSections();
            this.attachContentSectionListeners();
        }
    }
    attachContentSectionListeners() {
        const sections = this.overlay?.querySelectorAll('.sm-neighborhood-content-section');
        sections?.forEach((section) => {
            const header = section.querySelector('.sm-neighborhood-section-header');
            const sectionId = section.getAttribute('data-content-section');
            header?.addEventListener('click', () => {
                const isCollapsed = section.classList.toggle('collapsed');
                if (sectionId) {
                    if (isCollapsed) {
                        this.expandedContentSections.delete(sectionId);
                    }
                    else {
                        this.expandedContentSections.add(sectionId);
                    }
                }
            });
        });
    }
    updateDisclaimers() {
        const container = this.overlay?.querySelector('#sm-neighborhood-disclaimers');
        if (!container)
            return;
        // Destroy previous disclaimers
        if (this.disclaimers) {
            this.disclaimers.destroy();
        }
        this.disclaimers = new Disclaimers({
            container,
            showIdx: false, // No IDX disclaimer for neighborhood info
            showNeighborhood: true, // Show neighborhood-specific disclaimer
        });
        this.disclaimers.render();
    }
    render() {
        this.cleanup();
        this.overlay = document.createElement('div');
        this.overlay.className = 'sm-neighborhood-overlay';
        this.overlay.innerHTML = this.buildModalHtml();
        this.options.container.appendChild(this.overlay);
        this.attachEventListeners();
    }
    buildModalHtml() {
        const data = this.data;
        const title = data.name || 'Loading...';
        const subtitle = data.regionName || '';
        return `
      <div class="sm-neighborhood-modal">
        <div class="sm-neighborhood-header">
          <div class="sm-neighborhood-header-left">
            <h2 class="sm-neighborhood-title">${this.escapeHtml(title)}</h2>
            ${subtitle ? `<p class="sm-neighborhood-subtitle">${this.escapeHtml(subtitle)}</p>` : ''}
          </div>
          <div class="sm-neighborhood-header-right">
            ${this.buildActionButtons()}
            <button class="sm-neighborhood-close" aria-label="Close">&times;</button>
          </div>
        </div>
        <div class="sm-neighborhood-tabs">
          <button class="sm-neighborhood-tab active" data-tab="overview">Overview</button>
          <button class="sm-neighborhood-tab" data-tab="forSale">For Sale</button>
          <button class="sm-neighborhood-tab" data-tab="salesData">Sales Data</button>
          <button class="sm-neighborhood-tab" data-tab="latestActivity">Latest Activity</button>
          ${this.hasAgentContentTab() ? '<button class="sm-neighborhood-tab" data-tab="content">Content</button>' : ''}
        </div>
        <div class="sm-neighborhood-body">
          <!-- Overview Tab -->
          <div class="sm-neighborhood-tab-content active" data-tab-content="overview">
            ${this.buildOverviewTab()}
          </div>

          <!-- For Sale Tab -->
          <div class="sm-neighborhood-tab-content" data-tab-content="forSale">
            ${this.buildForSaleTab()}
          </div>

          <!-- Sales Data Tab -->
          <div class="sm-neighborhood-tab-content" data-tab-content="salesData">
            ${this.buildSalesTab()}
          </div>

          <!-- Latest Activity Tab -->
          <div class="sm-neighborhood-tab-content" data-tab-content="latestActivity">
            <div class="sm-neighborhood-activity-container"></div>
          </div>

          <!-- Content Tab (Agent Insight - agent-only) -->
          ${this.hasAgentContentTab() ? `
            <div class="sm-neighborhood-tab-content" data-tab-content="content">
              <div class="sm-neighborhood-content-tab-container"></div>
            </div>
          ` : ''}
        </div>
      </div>
    `;
    }
    hasAgentContentTab() {
        return !!(this.options.onLoadAgentNote && this.options.onSaveAgentNote);
    }
    buildActionButtons() {
        const data = this.data;
        const neighborhoodId = data?.id;
        const buttons = [];
        // Favorite button (heart)
        if (this.options.showFavoriteButton && neighborhoodId) {
            const isFavorite = this.options.isFavorite?.(neighborhoodId) ?? false;
            buttons.push(`
        <button class="sm-neighborhood-action-btn sm-neighborhood-favorite${isFavorite ? ' active' : ''}" aria-label="${isFavorite ? 'Remove from favorites' : 'Add to favorites'}" title="${isFavorite ? 'Remove from favorites' : 'Add to favorites'}" data-id="${neighborhoodId}">
          <svg viewBox="0 0 24 24" fill="${isFavorite ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
        </button>
      `);
        }
        // Alert button (bell) - labeled with visible text (not just a hover
        // title, which mobile users never see) so the value prop reads at a
        // glance instead of requiring a guess at what the icon means.
        if (this.options.showAlertButton && neighborhoodId) {
            const isAlerted = this.options.isAlerted?.(neighborhoodId) ?? false;
            buttons.push(`
        <button class="sm-neighborhood-action-btn sm-neighborhood-alert sm-neighborhood-alert-labeled${isAlerted ? ' active' : ''}" aria-label="${isAlerted ? 'Remove alert' : 'Set alert'}" title="${isAlerted ? 'Remove alert' : 'Get notified of new listings here'}" data-id="${neighborhoodId}">
          <svg viewBox="0 0 24 24" fill="${isAlerted ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
          <span class="sm-neighborhood-alert-label">${isAlerted ? 'Notified' : 'Notify me'}</span>
        </button>
      `);
        }
        // Share button
        buttons.push(`
      <button class="sm-neighborhood-action-btn sm-neighborhood-share" aria-label="Share" title="Share">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="18" cy="5" r="3"></circle>
          <circle cx="6" cy="12" r="3"></circle>
          <circle cx="18" cy="19" r="3"></circle>
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
        </svg>
      </button>
    `);
        // Agent Pick (Compile mode) - see PropertyModal's identical shopping-
        // cart treatment (per Todd 2026-10-26).
        if (this.options.showAgentPickButton && neighborhoodId) {
            const isPicked = this.options.isAgentPicked?.(neighborhoodId) ?? false;
            buttons.push(`
        <button class="sm-neighborhood-action-btn sm-neighborhood-agent-pick${isPicked ? ' active' : ''}" aria-label="${isPicked ? 'Remove from cart' : 'Add to cart'}" title="${isPicked ? 'Remove from cart' : 'Add to cart'}" data-id="${neighborhoodId}">
          <svg viewBox="0 0 24 24" fill="${isPicked ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
        </button>
      `);
        }
        return buttons.join('');
    }
    buildOverviewTab() {
        return `
      <!-- Stats Row -->
      <div class="sm-neighborhood-stats" id="sm-neighborhood-stats">
        ${this.buildStatsRow()}
      </div>

      <!-- Gallery -->
      <div class="sm-neighborhood-gallery" id="sm-neighborhood-gallery">
        ${this.buildGallery()}
      </div>

      <!-- AI-Generated Content Sections -->
      <div id="sm-neighborhood-content-sections">
        ${this.buildContentSections()}
      </div>

      <!-- Agent Insights (approved agent_notes) -->
      <div id="sm-neighborhood-agent-insights">
        ${this.buildAgentInsightsSection()}
      </div>

      <!-- Structured Info Sections (Building, Amenities, etc.) -->
      <div id="sm-neighborhood-sections"></div>

      <!-- View on Map Button -->
      <button class="sm-neighborhood-view-map-btn" id="sm-neighborhood-view-map-btn">
        View on Map
      </button>

      <!-- Disclaimers -->
      <div id="sm-neighborhood-disclaimers"></div>
    `;
    }
    buildContentSections() {
        const content = this.neighborhood?.content;
        if (!content) {
            return '';
        }
        let html = '';
        for (const section of CONTENT_SECTIONS) {
            const sectionContent = content[section.id];
            if (!sectionContent)
                continue;
            const isExpanded = this.expandedContentSections.has(section.id);
            html += `
        <div class="sm-neighborhood-content-section${isExpanded ? '' : ' collapsed'}" data-content-section="${section.id}">
          <button class="sm-neighborhood-section-header">
            <span>${section.icon} ${section.name}</span>
            <svg class="sm-neighborhood-section-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#000" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          </button>
          <div class="sm-neighborhood-section-content">
            <div class="sm-neighborhood-content-html">${sectionContent}</div>
          </div>
        </div>
      `;
        }
        return html;
    }
    /**
     * Approved agent_notes for this neighborhood ("Agent Insights") -
     * Phase 3 of docs/plans/agent-content-workflow-2026-09-28.md. Distinct
     * from the "Content" tab elsewhere in this modal, which is a logged-in
     * agent's own private note editor (onLoadAgentNote/onSaveAgentNote) -
     * this section is public, read-only, and shows every agent's approved
     * note together. noteHtml is sanitized server-side at write time (see
     * workers/agent-api's handleAgentNotes), safe to render as-is here.
     */
    buildAgentInsightsSection() {
        const insights = this.neighborhood?.agentInsights;
        if (!insights || insights.length === 0) {
            return '';
        }
        return `
      <div class="sm-agent-insights">
        <h3 class="sm-agent-insights-title">Agent Insights</h3>
        ${insights.map((insight) => `
          <div class="sm-agent-insight">
            <div class="sm-agent-insight-body">${insight.noteHtml}</div>
            <a class="sm-agent-insight-byline" href="https://${encodeURIComponent(insight.agentSubdomain)}.slowmaui.com" target="_blank" rel="noopener noreferrer">— ${this.escapeHtml(insight.agentName)}</a>
          </div>
        `).join('')}
      </div>
    `;
    }
    buildStatsRow() {
        const data = this.data;
        const neighborhood = this.neighborhood;
        const stats = [];
        const totalUnits = neighborhood?.totalUnits || data?.totalUnits;
        if (totalUnits) {
            stats.push({ value: totalUnits.toLocaleString(), label: 'Units' });
        }
        // forSaleCount comes from data (NeighborhoodSummary), not from full Neighborhood
        const forSale = data?.forSaleCount;
        if (forSale !== undefined) {
            stats.push({ value: String(forSale), label: 'For Sale' });
        }
        if (neighborhood?.yearBuilt) {
            stats.push({ value: String(neighborhood.yearBuilt), label: 'Year Built' });
        }
        if (neighborhood?.strAllowed !== undefined) {
            stats.push({ value: neighborhood.strAllowed ? 'Yes' : 'No', label: 'STR Allowed' });
        }
        const walkScore = neighborhood?.info?.location?.walkScore;
        if (walkScore !== undefined) {
            stats.push({ value: String(walkScore), label: 'Walk Score' });
        }
        // Three numbers (elementary/middle/high, in that fixed order) rather
        // than labeled rows - compact enough for a stat tile, and the order
        // itself conveys which level is which. The full Schools section lower
        // down has names/links; this is just the at-a-glance rating.
        const schools = neighborhood?.info?.schools;
        if (schools) {
            const schoolRatings = [schools.elementary, schools.middle, schools.high]
                .map((s) => s?.rankStars)
                .filter((n) => n !== undefined);
            if (schoolRatings.length > 0) {
                stats.push({ value: schoolRatings.join(' · '), label: 'Schools' });
            }
        }
        if (stats.length === 0) {
            return `
        <div class="sm-neighborhood-loading">
          <div class="sm-neighborhood-loading-spinner"></div>
          Loading...
        </div>
      `;
        }
        return stats.map(stat => `
      <div class="sm-neighborhood-stat">
        <div class="sm-neighborhood-stat-value">${stat.value}</div>
        <div class="sm-neighborhood-stat-label">${stat.label}</div>
      </div>
    `).join('');
    }
    updateStatsRow() {
        const container = this.overlay?.querySelector('#sm-neighborhood-stats');
        if (container) {
            container.innerHTML = this.buildStatsRow();
        }
    }
    buildGallery() {
        const neighborhood = this.neighborhood;
        const data = this.data;
        const media = neighborhood?.media?.filter(m => m.type === 'image') || [];
        const photos = media.map(m => m.url);
        // Add primary image from data if no media yet
        if (photos.length === 0 && data?.primaryImageUrl) {
            photos.push(data.primaryImageUrl);
        }
        if (photos.length === 0) {
            // No photos - fall back to the neighborhood's boundary on a real map
            // (same approach GeographyModal already uses for region/district,
            // which never had a photo gallery at all - see its initEntityMap).
            // Only possible once the full Neighborhood record has loaded (the
            // lightweight NeighborhoodModalData passed to open() has no polygon),
            // so this still shows the plain placeholder on the very first render.
            const polygon = neighborhood?.polygon;
            if (polygon && polygon.length >= 3) {
                return `
          <div class="sm-neighborhood-gallery-main">
            <div id="sm-neighborhood-gallery-map" class="sm-neighborhood-gallery-map"></div>
          </div>
        `;
            }
            return `
        <div class="sm-neighborhood-gallery-main">
          <div class="sm-neighborhood-gallery-placeholder">${neighborhood ? 'No images available' : ''}</div>
        </div>
      `;
        }
        const currentPhoto = photos[this.currentPhotoIndex] || photos[0];
        const showNav = photos.length > 1;
        return `
      <div class="sm-neighborhood-gallery-main">
        <img src="${this.escapeHtml(currentPhoto)}" alt="Neighborhood photo">
        ${showNav ? `
          <button class="sm-neighborhood-gallery-nav sm-neighborhood-gallery-prev" data-dir="prev">&lt;</button>
          <button class="sm-neighborhood-gallery-nav sm-neighborhood-gallery-next" data-dir="next">&gt;</button>
          <div class="sm-neighborhood-gallery-counter">${this.currentPhotoIndex + 1} / ${photos.length}</div>
        ` : ''}
      </div>
      ${photos.length > 1 ? `
        <div class="sm-neighborhood-gallery-thumbs">
          ${photos.map((photo, i) => `
            <img
              src="${this.escapeHtml(photo)}"
              class="sm-neighborhood-gallery-thumb${i === this.currentPhotoIndex ? ' active' : ''}"
              data-index="${i}"
              alt="Thumbnail ${i + 1}"
            >
          `).join('')}
        </div>
      ` : ''}
    `;
    }
    updateGallery() {
        const container = this.overlay?.querySelector('#sm-neighborhood-gallery');
        if (container) {
            container.innerHTML = this.buildGallery();
            this.attachGalleryListeners();
            void this.initGalleryMap();
        }
    }
    // Draws the neighborhood's boundary into the #sm-neighborhood-gallery-map
    // div buildGallery() renders when there are no photos. No-op if that div
    // isn't present (i.e. real photos are showing instead). Mirrors
    // GeographyModal's initEntityMap - a real google.maps.Map + Polygon
    // overlay rather than a Static Maps image, since the JS Maps API is
    // already loaded by the main map this modal is opened from.
    async initGalleryMap() {
        const container = this.overlay?.querySelector('#sm-neighborhood-gallery-map');
        if (!container)
            return;
        const polygon = this.neighborhood?.polygon;
        if (!polygon || polygon.length < 3)
            return;
        if (this.galleryMapPolygon) {
            this.galleryMapPolygon.setMap(null);
            this.galleryMapPolygon = null;
        }
        this.galleryMap = null;
        try {
            const { Map } = await google.maps.importLibrary('maps');
            // The modal may have been closed (its overlay removed) while this
            // import was in flight - container is a direct DOM reference captured
            // before the await, so it stays "valid" (non-null) even once detached.
            // Building a full google.maps.Map in a dead container leaks the whole
            // instance forever: nothing ever calls this function's cleanup path
            // again for this already-finished close() cycle. Confirmed live via a
            // heap snapshot retainer trace (2026-10) - a stray Map + its internal
            // listener registry, anchored in a Detached <div>. See the identical
            // fix in GeographyModal.initEntityMap().
            if (!container.isConnected)
                return;
            this.galleryMap = new Map(container, {
                mapTypeId: 'roadmap',
                disableDefaultUI: true,
                zoomControl: true,
                gestureHandling: 'cooperative',
            });
            this.galleryMapPolygon = new google.maps.Polygon({
                paths: polygon,
                strokeColor: '#2c5530',
                strokeOpacity: 0.9,
                strokeWeight: 2,
                fillColor: '#2c5530',
                fillOpacity: 0.15,
                clickable: false,
            });
            this.galleryMapPolygon.setMap(this.galleryMap);
            const bounds = new google.maps.LatLngBounds();
            polygon.forEach(pt => bounds.extend(pt));
            this.galleryMap.fitBounds(bounds, 24);
        }
        catch (error) {
            console.error('Failed to load neighborhood boundary map:', error);
            container.innerHTML = '<div class="sm-neighborhood-gallery-placeholder">Map unavailable</div>';
        }
    }
    updateSections() {
        if (!this.neighborhood)
            return;
        const container = this.overlay?.querySelector('#sm-neighborhood-sections');
        if (!container)
            return;
        this.sections = new NeighborhoodSections({
            container,
            neighborhood: this.neighborhood,
        });
        this.sections.render();
    }
    buildForSaleTab() {
        if (this.listings.length === 0) {
            return `
        <div class="sm-neighborhood-loading" id="sm-neighborhood-listings-loading">
          <div class="sm-neighborhood-loading-spinner"></div>
          Loading listings...
        </div>
        <div class="sm-neighborhood-listings" id="sm-neighborhood-listings" style="display: none;"></div>
      `;
        }
        return `<div class="sm-neighborhood-listings" id="sm-neighborhood-listings">${this.buildListingsContent()}</div>`;
    }
    buildListingsContent() {
        if (this.listings.length === 0) {
            return '<div class="sm-neighborhood-listing-empty">No active listings</div>';
        }
        return this.listings.map(listing => this.buildListingCard(listing)).join('');
    }
    buildListingCard(listing) {
        const price = this.formatPrice(listing.listPrice);
        const photo = listing.photoUrl || listing.primaryPhotoUrl;
        const imageHtml = photo
            ? `<img class="sm-neighborhood-listing-image" src="${this.escapeHtml(photo)}" alt="Property photo" loading="lazy">`
            : `<div class="sm-neighborhood-listing-image-placeholder"></div>`;
        // This tab fetches Active+Pending together (see onLoadListings in
        // main.ts) so it can show "still worth a look" listings under
        // contract, not just strictly-Active ones - the badge is what keeps
        // that combined count from reading as a data bug (confirmed live:
        // Hokulani Golf Villas showed 9 here vs a 7-count "For Sale" stat
        // elsewhere, which is 7 Active + 2 Pending, not a mismatch).
        const pendingBadge = listing.status === 'Pending'
            ? `<span class="sm-neighborhood-listing-status-badge pending">Pending</span>`
            : '';
        return `
      <div class="sm-neighborhood-listing-card" data-listing-id="${listing.id}">
        ${imageHtml}
        <div class="sm-neighborhood-listing-info">
          <div class="sm-neighborhood-listing-price">${price}${pendingBadge}</div>
          <div class="sm-neighborhood-listing-address">${this.escapeHtml(formatListingAddress(listing))}</div>
          <div class="sm-neighborhood-listing-stats">
            ${listing.bedrooms ? `<span>${listing.bedrooms} bd</span>` : ''}
            ${listing.bathrooms ? `<span>${listing.bathrooms} ba</span>` : ''}
            ${listing.livingArea ? `<span>${listing.livingArea.toLocaleString()} sqft</span>` : ''}
          </div>
        </div>
      </div>
    `;
    }
    updateListingsTab() {
        const loading = this.overlay?.querySelector('#sm-neighborhood-listings-loading');
        const container = this.overlay?.querySelector('#sm-neighborhood-listings');
        if (loading) {
            loading.remove();
        }
        if (container) {
            container.style.display = '';
            container.innerHTML = this.buildListingsContent();
            this.attachListingListeners();
        }
    }
    buildSalesTab() {
        if (!this.salesData) {
            return `
        <div class="sm-neighborhood-loading" id="sm-neighborhood-sales-loading">
          <div class="sm-neighborhood-loading-spinner"></div>
          Loading sales data...
        </div>
        <div id="sm-neighborhood-sales-content" style="display: none;"></div>
      `;
        }
        return `<div id="sm-neighborhood-sales-content">${this.buildSalesContent()}</div>`;
    }
    buildSalesContent() {
        if (!this.salesData) {
            return '<div class="sm-neighborhood-sales-empty">No sales data available</div>';
        }
        // If chart data is available, just return a container for the chart
        if (this.salesData.chartData) {
            return `
        <div id="sm-sales-chart-container"></div>
        <div id="sm-neighborhood-sales-disclaimers"></div>
      `;
        }
        // Fallback to legacy stats display
        const data = this.salesData;
        const stats = [];
        if (data.salesCount1yr !== undefined) {
            stats.push({ value: String(data.salesCount1yr), label: 'Sales (1 yr)' });
        }
        if (data.avgSalePrice !== undefined) {
            stats.push({
                value: this.formatPrice(data.avgSalePrice),
                label: 'Avg Sale Price',
                trend: data.salesTrend,
            });
        }
        if (data.medianSalePrice !== undefined) {
            stats.push({ value: this.formatPrice(data.medianSalePrice), label: 'Median Price' });
        }
        if (data.avgDaysOnMarket !== undefined) {
            stats.push({ value: String(data.avgDaysOnMarket), label: 'Avg Days on Market' });
        }
        if (data.pricePerSqft !== undefined) {
            stats.push({ value: `$${data.pricePerSqft.toLocaleString()}`, label: 'Price/Sq Ft' });
        }
        if (stats.length === 0) {
            return '<div class="sm-neighborhood-sales-empty">No sales data available</div>';
        }
        return `
      <div class="sm-neighborhood-sales-stats">
        ${stats.map(stat => `
          <div class="sm-neighborhood-sales-stat">
            <div class="sm-neighborhood-sales-stat-value">
              ${stat.value}
              ${stat.trend ? `
                <span class="sm-neighborhood-sales-trend ${stat.trend}">
                  ${stat.trend === 'up' ? '&#9650;' : stat.trend === 'down' ? '&#9660;' : '&#8212;'}
                </span>
              ` : ''}
            </div>
            <div class="sm-neighborhood-sales-stat-label">${stat.label}</div>
          </div>
        `).join('')}
      </div>
      <div id="sm-neighborhood-sales-disclaimers"></div>
    `;
    }
    updateSalesTab() {
        const loading = this.overlay?.querySelector('#sm-neighborhood-sales-loading');
        const container = this.overlay?.querySelector('#sm-neighborhood-sales-content');
        if (loading) {
            loading.remove();
        }
        if (container) {
            container.style.display = '';
            container.innerHTML = this.buildSalesContent();
            // Initialize SalesChart if chart data is available
            if (this.salesData?.chartData) {
                const chartContainer = container.querySelector('#sm-sales-chart-container');
                if (chartContainer) {
                    // Destroy previous chart if exists
                    if (this.salesChart) {
                        this.salesChart.destroy();
                    }
                    this.salesChart = new SalesChart({
                        container: chartContainer,
                        data: this.salesData.chartData,
                        // A single neighborhood is almost always one property type
                        // throughout (confirmed live 2026-10: 172/173 classifiable
                        // neighborhoods were ≥90% House or ≥90% Condo) - see
                        // SalesChartOptions.showPropertyTypeFilter's comment. Unlike
                        // GeographyModal (district/region/county), which keeps this on.
                        showPropertyTypeFilter: false,
                        onDataPointClick: (point) => {
                            // Handle click on data point - could open property modal
                            console.log('Sales chart point clicked:', point);
                        },
                    });
                }
            }
            // Sales Data aggregates sales+listings across a scope/time window
            // (unlike a single-listing IDX display), so the aggregate-data
            // disclaimer replaces idx as the always-visible one here; mls/general/
            // ai stay behind the toggle since this is still MLS-derived content.
            const disclaimersContainer = container.querySelector('#sm-neighborhood-sales-disclaimers');
            if (disclaimersContainer) {
                this.salesDisclaimers?.destroy();
                this.salesDisclaimers = new Disclaimers({
                    container: disclaimersContainer,
                    showIdx: false,
                    showAggregate: true,
                });
                this.salesDisclaimers.render();
            }
        }
    }
    attachEventListeners() {
        if (!this.overlay)
            return;
        // Close button
        const closeBtn = this.overlay.querySelector('.sm-neighborhood-close');
        closeBtn?.addEventListener('click', () => this.close());
        // Backdrop click
        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) {
                this.close();
            }
        });
        // Escape key
        document.addEventListener('keydown', this.handleKeydown);
        // Tab switching
        const tabs = this.overlay.querySelectorAll('.sm-neighborhood-tab');
        tabs.forEach(tab => {
            tab.addEventListener('click', () => {
                const tabId = tab.dataset.tab;
                this.switchTab(tabId);
            });
        });
        // View on Map button
        const viewMapBtn = this.overlay.querySelector('#sm-neighborhood-view-map-btn');
        viewMapBtn?.addEventListener('click', () => {
            if (this.neighborhood && this.options.onViewOnMap) {
                this.options.onViewOnMap(this.neighborhood);
                this.close();
            }
        });
        // Gallery listeners
        this.attachGalleryListeners();
        // Share button
        const shareBtn = this.overlay.querySelector('.sm-neighborhood-share');
        shareBtn?.addEventListener('click', () => this.handleShare());
        // Favorite button
        const favoriteBtn = this.overlay.querySelector('.sm-neighborhood-favorite');
        favoriteBtn?.addEventListener('click', () => this.handleFavoriteToggle());
        // Alert button
        const alertBtn = this.overlay.querySelector('.sm-neighborhood-alert');
        alertBtn?.addEventListener('click', () => this.handleAlertToggle());
        // Agent Pick button
        const agentPickBtn = this.overlay.querySelector('.sm-neighborhood-agent-pick');
        agentPickBtn?.addEventListener('click', () => this.handleAgentPickToggle());
    }
    handleFavoriteToggle() {
        const neighborhoodId = this.data?.id;
        if (!neighborhoodId || !this.options.onFavoriteToggle)
            return;
        const btn = this.overlay?.querySelector('.sm-neighborhood-favorite');
        if (!btn)
            return;
        const isCurrentlyFavorite = btn.classList.contains('active');
        const newState = !isCurrentlyFavorite;
        // Update UI immediately
        btn.classList.toggle('active', newState);
        btn.setAttribute('aria-label', newState ? 'Remove from favorites' : 'Add to favorites');
        btn.setAttribute('title', newState ? 'Remove from favorites' : 'Add to favorites');
        // Update SVG fill
        const svg = btn.querySelector('svg');
        if (svg) {
            svg.setAttribute('fill', newState ? 'currentColor' : 'none');
        }
        // Notify callback
        this.options.onFavoriteToggle(neighborhoodId, newState);
        // Show toast
        this.showShareToast(newState ? 'Added to favorites' : 'Removed from favorites');
    }
    handleAlertToggle() {
        const neighborhoodId = this.data?.id;
        if (!neighborhoodId)
            return;
        // Alerts require login - checked before any visual change, not after.
        // No optimistic flip here; onAlertAuthRequired (main.ts) opens the auth
        // modal, and setAlertState() below is what actually updates this button
        // once sign-in completes and the alert is created for real.
        if (this.options.isLoggedIn && !this.options.isLoggedIn()) {
            this.options.onAlertAuthRequired?.(neighborhoodId);
            return;
        }
        if (!this.options.onAlertToggle)
            return;
        const btn = this.overlay?.querySelector('.sm-neighborhood-alert');
        if (!btn)
            return;
        const isCurrentlyAlerted = btn.classList.contains('active');
        const newState = !isCurrentlyAlerted;
        this.applyAlertVisualState(btn, newState);
        // Notify callback
        this.options.onAlertToggle(neighborhoodId, newState);
        // Show toast
        this.showShareToast(newState ? 'Alert set for this neighborhood' : 'Alert removed');
    }
    // No toast here (unlike Favorite/Alert above) - the AgentDrawer already
    // toasts "Added/Removed <label>" once its own add/remove call resolves.
    handleAgentPickToggle() {
        const neighborhoodId = this.data?.id;
        if (!neighborhoodId || !this.options.onAgentPickToggle)
            return;
        const btn = this.overlay?.querySelector('.sm-neighborhood-agent-pick');
        if (!btn)
            return;
        const isCurrentlyPicked = btn.classList.contains('active');
        const newState = !isCurrentlyPicked;
        btn.classList.toggle('active', newState);
        btn.setAttribute('aria-label', newState ? 'Remove from Agent Picks' : 'Add to Agent Picks');
        btn.setAttribute('title', newState ? 'Remove from Agent Picks' : 'Add to Agent Picks');
        const svg = btn.querySelector('svg');
        const existingCheck = svg?.querySelector('polyline');
        if (newState && svg && !existingCheck) {
            svg.insertAdjacentHTML('beforeend', '<polyline points="7 12.5 10.5 16 17 8.5"></polyline>');
        }
        else if (!newState && existingCheck) {
            existingCheck.remove();
        }
        this.options.onAgentPickToggle(neighborhoodId, newState, this.data?.name || 'neighborhood');
    }
    applyAlertVisualState(btn, newState) {
        btn.classList.toggle('active', newState);
        btn.setAttribute('aria-label', newState ? 'Remove alert' : 'Set alert');
        btn.setAttribute('title', newState ? 'Remove alert' : 'Get notified of new listings here');
        const svg = btn.querySelector('svg');
        if (svg) {
            svg.setAttribute('fill', newState ? 'currentColor' : 'none');
        }
        const label = btn.querySelector('.sm-neighborhood-alert-label');
        if (label) {
            label.textContent = newState ? 'Notified' : 'Notify me';
        }
    }
    /**
     * Updates the alert button's visual state without a click - used after a
     * pending sign-in completes and the alert was actually created for the
     * neighborhood this modal is currently showing. No-ops if the modal has
     * since moved on to a different neighborhood.
     */
    setAlertState(neighborhoodId, isAlerted) {
        if (this.data?.id !== neighborhoodId)
            return;
        const btn = this.overlay?.querySelector('.sm-neighborhood-alert');
        if (!btn)
            return;
        this.applyAlertVisualState(btn, isAlerted);
    }
    async handleShare() {
        if (!this.neighborhood)
            return;
        const title = this.neighborhood.name || 'Neighborhood';
        const url = window.location.href;
        const text = `${title} - South Maui Real Estate`;
        // Try native share API first (mobile)
        if (navigator.share) {
            try {
                await navigator.share({ title, text, url });
                return;
            }
            catch (err) {
                // User cancelled or share failed, fall back to clipboard
                if (err.name === 'AbortError')
                    return;
            }
        }
        // Fall back to clipboard
        try {
            await navigator.clipboard.writeText(url);
            this.showShareToast('Link copied to clipboard');
        }
        catch {
            // Final fallback: show URL in prompt
            prompt('Copy this link:', url);
        }
    }
    showShareToast(message) {
        // Create toast element
        const toast = document.createElement('div');
        toast.className = 'sm-share-toast';
        toast.textContent = message;
        document.body.appendChild(toast);
        // Trigger animation
        requestAnimationFrame(() => {
            toast.classList.add('show');
        });
        // Remove after delay
        setTimeout(() => {
            toast.classList.remove('show');
            setTimeout(() => toast.remove(), 300);
        }, 2000);
    }
    attachGalleryListeners() {
        if (!this.overlay)
            return;
        // Navigation buttons
        const navBtns = this.overlay.querySelectorAll('.sm-neighborhood-gallery-nav');
        navBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const dir = btn.dataset.dir;
                this.navigateGallery(dir === 'next' ? 1 : -1);
            });
        });
        // Thumbnails
        const thumbs = this.overlay.querySelectorAll('.sm-neighborhood-gallery-thumb');
        thumbs.forEach(thumb => {
            thumb.addEventListener('click', () => {
                const index = parseInt(thumb.dataset.index || '0', 10);
                this.currentPhotoIndex = index;
                this.updateGallery();
            });
        });
    }
    attachListingListeners() {
        const cards = this.overlay?.querySelectorAll('.sm-neighborhood-listing-card');
        cards?.forEach(card => {
            card.addEventListener('click', () => {
                const listingId = card.dataset.listingId;
                const listing = this.listings.find(l => l.id === listingId);
                if (listing && this.options.onListingClick) {
                    this.options.onListingClick(listing);
                }
            });
        });
    }
    navigateGallery(direction) {
        const media = this.neighborhood?.media?.filter(m => m.type === 'image') || [];
        let photos = media.map(m => m.url);
        if (photos.length === 0 && this.data?.primaryImageUrl) {
            photos = [this.data.primaryImageUrl];
        }
        if (photos.length <= 1)
            return;
        this.currentPhotoIndex = (this.currentPhotoIndex + direction + photos.length) % photos.length;
        this.updateGallery();
    }
    switchTab(tabId) {
        if (this.currentTab === tabId)
            return;
        this.currentTab = tabId;
        // Update tab buttons
        const tabs = this.overlay?.querySelectorAll('.sm-neighborhood-tab');
        tabs?.forEach(tab => {
            tab.classList.toggle('active', tab.dataset.tab === tabId);
        });
        // Update tab content
        const contents = this.overlay?.querySelectorAll('.sm-neighborhood-tab-content');
        contents?.forEach(content => {
            content.classList.toggle('active', content.dataset.tabContent === tabId);
        });
        // Load data for the tab if needed
        if (tabId === 'forSale' && this.listings.length === 0 && this.data) {
            this.loadListings(this.data.id);
        }
        else if (tabId === 'salesData' && !this.salesData && this.data) {
            this.loadSalesData(this.data.id);
        }
        else if (tabId === 'latestActivity' && this.data) {
            this.loadActivityTab(this.data.id);
        }
        else if (tabId === 'content' && !this.agentNoteLoaded && this.data) {
            void this.loadContentTab(this.data.id);
        }
    }
    /**
     * Content tab (Agent Insight) - loads the agent's existing note (if any)
     * on first switch only. Re-switching to this tab after that does NOT
     * re-render, so an unsaved in-progress edit in the textarea survives
     * navigating to another tab and back.
     */
    async loadContentTab(neighborhoodId) {
        const container = this.overlay?.querySelector('.sm-neighborhood-content-tab-container');
        if (!container || !this.options.onLoadAgentNote)
            return;
        container.innerHTML = `<div class="sm-neighborhood-loading"><div class="sm-neighborhood-loading-spinner"></div>Loading...</div>`;
        try {
            const note = await this.options.onLoadAgentNote(neighborhoodId);
            this.agentNoteId = note?.id ?? null;
            this.agentNoteText = note?.noteHtml ?? '';
            this.agentNoteStatus = note?.status ?? null;
            this.agentNoteAdminNote = note?.adminNote ?? null;
        }
        catch (error) {
            console.error('Failed to load agent note:', error);
        }
        this.agentNoteLoaded = true;
        container.innerHTML = `
      <p class="sm-neighborhood-content-tab-hint">Add your own insight about this neighborhood - saved under your agent profile.</p>
      <div class="sm-neighborhood-note-status-row">${this.renderAgentNoteStatusBadge()}</div>
      <textarea class="sm-neighborhood-content-textarea" placeholder="Share what makes this neighborhood special...">${this.escapeHtml(this.agentNoteText)}</textarea>
      <div class="sm-neighborhood-content-tab-actions">
        <span class="sm-neighborhood-content-tab-status"></span>
        <button class="sm-neighborhood-content-save-btn" type="button">Save</button>
      </div>
    `;
        this.attachContentTabListeners(neighborhoodId);
    }
    /**
     * Status pill for the Content tab, reflecting the agent_notes moderation
     * state (migration 050) - pending until an admin reviews it in the Review
     * Queue, approved once it's live in Agent Insights, or rejected (with the
     * admin's reason, if given). No badge for a note that's never been saved.
     */
    renderAgentNoteStatusBadge() {
        if (!this.agentNoteId || !this.agentNoteStatus)
            return '';
        const labels = {
            pending: 'Pending review',
            approved: 'Live',
            rejected: 'Rejected',
        };
        const badge = `<span class="sm-neighborhood-note-badge sm-neighborhood-note-badge-${this.agentNoteStatus}">${labels[this.agentNoteStatus]}</span>`;
        if (this.agentNoteStatus === 'rejected' && this.agentNoteAdminNote) {
            return `${badge} <span class="sm-neighborhood-note-rejection-reason">${this.escapeHtml(this.agentNoteAdminNote)}</span>`;
        }
        return badge;
    }
    attachContentTabListeners(neighborhoodId) {
        const container = this.overlay?.querySelector('.sm-neighborhood-content-tab-container');
        const saveBtn = container?.querySelector('.sm-neighborhood-content-save-btn');
        const textarea = container?.querySelector('.sm-neighborhood-content-textarea');
        const status = container?.querySelector('.sm-neighborhood-content-tab-status');
        if (!saveBtn || !textarea)
            return;
        saveBtn.addEventListener('click', async () => {
            if (!this.options.onSaveAgentNote)
                return;
            const noteHtml = textarea.value.trim();
            if (!noteHtml)
                return;
            saveBtn.disabled = true;
            saveBtn.textContent = 'Saving...';
            try {
                const result = await this.options.onSaveAgentNote(neighborhoodId, noteHtml, this.agentNoteId);
                this.agentNoteId = result.id;
                this.agentNoteText = noteHtml;
                // Every save (create or edit) resets the note to 'pending' server-side
                // (see workers/agent-api's handleAgentNotes) - mirror that here so the
                // badge doesn't keep showing a stale 'approved'/'rejected' state.
                this.agentNoteStatus = 'pending';
                this.agentNoteAdminNote = null;
                const statusRow = container?.querySelector('.sm-neighborhood-note-status-row');
                if (statusRow)
                    statusRow.innerHTML = this.renderAgentNoteStatusBadge();
                if (status) {
                    status.textContent = 'Saved';
                    setTimeout(() => { if (status)
                        status.textContent = ''; }, 2000);
                }
            }
            catch (error) {
                console.error('Failed to save agent note:', error);
                if (status)
                    status.textContent = 'Failed to save';
            }
            finally {
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save';
            }
        });
    }
    loadActivityTab(neighborhoodId) {
        if (!this.options.onLoadActivity)
            return;
        const container = this.overlay?.querySelector('.sm-neighborhood-activity-container');
        if (!container)
            return;
        if (!this.activityTable) {
            this.activityTable = new ActivityTable({
                container,
                showNeighborhoodColumn: false,
                onLoadActivity: (days) => this.options.onLoadActivity(neighborhoodId, days),
                ...(this.options.onActivityListingClick ? { onListingClick: this.options.onActivityListingClick } : {}),
            });
        }
        void this.activityTable.load();
    }
    handleKeydown = (e) => {
        if (e.key === 'Escape' && this.overlay?.classList.contains('active')) {
            this.close();
        }
    };
    show() {
        if (this.overlay) {
            void this.overlay.offsetHeight;
            this.overlay.classList.add('active');
            document.body.style.overflow = 'hidden';
        }
    }
    close() {
        // Only fire onClose when there's actually an open overlay (see same fix
        // in PropertyModal). Some callers' onClose handlers do a `pushState('/')`
        // which would otherwise nuke the URL on initial-load close() calls.
        if (!this.overlay)
            return;
        this.overlay.classList.remove('active');
        document.body.style.overflow = '';
        // Snapshot the instance being closed, then immediately detach `this`'s
        // fields from it - see GeographyModal.close()'s comment for why: without
        // this, a rapid reopen within the 300ms fade would have the deferred
        // cleanup() below tear down the NEW instance (whatever `this` points to
        // when it fires) while this closed instance's overlay and its live
        // google.maps.Map leak forever, un-removed from the DOM.
        const closed = this.snapshotResources();
        this.resetResourceFields();
        setTimeout(() => {
            this.cleanup(closed);
        }, 300);
        this.options.onClose?.();
    }
    snapshotResources() {
        return {
            overlay: this.overlay,
            galleryMap: this.galleryMap,
            galleryMapPolygon: this.galleryMapPolygon,
            sections: this.sections,
            salesChart: this.salesChart,
            disclaimers: this.disclaimers,
            salesDisclaimers: this.salesDisclaimers,
        };
    }
    resetResourceFields() {
        this.overlay = null;
        this.sections = null;
        this.salesChart = null;
        this.disclaimers = null;
        this.salesDisclaimers = null;
        this.galleryMap = null;
        this.galleryMapPolygon = null;
        this.expandedContentSections = new Set(['overview']);
        this.agentNoteId = null;
        this.agentNoteText = '';
        this.agentNoteStatus = null;
        this.agentNoteAdminNote = null;
        this.agentNoteLoaded = false;
    }
    /** Tears down a specific snapshot of resources - never `this.*` directly,
     * so it's safe to run after a rapid reopen has already moved `this`'s
     * fields on to a newer instance (see close()). */
    cleanup(resources = this.snapshotResources()) {
        resources.sections?.destroy();
        resources.salesChart?.destroy();
        resources.disclaimers?.destroy();
        resources.salesDisclaimers?.destroy();
        resources.galleryMapPolygon?.setMap(null);
        resources.overlay?.remove();
        if (resources.overlay === this.overlay) {
            this.resetResourceFields();
        }
    }
    destroy() {
        document.removeEventListener('keydown', this.handleKeydown);
        this.cleanup();
        this.data = null;
        this.neighborhood = null;
        this.listings = [];
        this.salesData = null;
    }
    formatPrice(amount) {
        if (amount >= 1000000) {
            return `$${(amount / 1000000).toFixed(amount % 1000000 === 0 ? 0 : 1)}M`;
        }
        return `$${Math.round(amount / 1000)}K`;
    }
    escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }
}
//# sourceMappingURL=NeighborhoodModal.js.map